import { FormValues } from '@monorepo/shared/componentsV2/fieldDataType/values/EditFieldValueFactory/types';
import { LOG_DATE_FIELD_NAME } from '@monorepo/shared/componentsV2/logs/types';
import {
  isDateOrNullish,
  isRequired,
  isTimeStringOrNullish,
} from '@monorepo/shared/utils/validators';
import { FieldDataType, FieldResponse } from 'mapistry-shared';
import type { LogEntriesFormValues } from '../conversionHelpers';

const isTimeOrNullish = isTimeStringOrNullish('HH:mm');

export type LogEntryFieldErrors = { [fieldId: string]: string };
export type LogEntriesFormErrors = {
  [logEntryId: string]: LogEntryFieldErrors;
};

function getBlockingDateLikeError(field: FieldResponse, value: unknown) {
  switch (field.type) {
    case FieldDataType.DATE:
    case FieldDataType.DATETIME:
      return isDateOrNullish(value);
    case FieldDataType.TIME:
      return isTimeOrNullish(value as string | null | undefined);
    default:
      return undefined;
  }
}

function validateLogEntryValues(
  entryValues: FormValues,
  logFields: FieldResponse[],
): LogEntryFieldErrors | undefined {
  const errors: LogEntryFieldErrors = {};

  const logDate = entryValues[LOG_DATE_FIELD_NAME];
  const logDateError = isRequired(logDate) ?? isDateOrNullish(logDate);
  if (logDateError) errors[LOG_DATE_FIELD_NAME] = logDateError;

  logFields.forEach((field) => {
    const value = entryValues[field.id];
    const blockingError = getBlockingDateLikeError(field, value);
    const error =
      blockingError ?? (field.isRequired ? isRequired(value) : undefined);
    if (error) errors[field.id] = error;
  });

  return Object.keys(errors).length ? errors : undefined;
}

export function validateLogEntriesFormValues(
  values: Partial<LogEntriesFormValues>,
  logFields: FieldResponse[] | undefined,
): LogEntriesFormErrors {
  const errorsByEntryId: LogEntriesFormErrors = {};

  Object.entries(values).forEach(([entryId, entryValues]) => {
    if (!entryValues) return;
    const entryErrors = validateLogEntryValues(entryValues, logFields ?? []);
    if (entryErrors) errorsByEntryId[entryId] = entryErrors;
  });

  return errorsByEntryId;
}
